/**
 * Hang a profile card off anything that represents a person.
 *
 * The trigger is a real `<button>` wrapping whatever you pass — an avatar, a
 * name, a roster row — because a click target that opens a panel has to be
 * reachable by Tab and Enter, and a `<span onClick>` is neither. Radix mounts
 * the content only while open, so `<ProfileCardPopover>` around every avatar in
 * a virtualised timeline costs one button each and no requests at all until
 * someone actually opens one.
 *
 * IT OPENS ON HOVER, not only on click, because that is how you read a chat:
 * you skim, you wonder who someone is, and you should not have to interrupt
 * yourself with a click to find out. Three details make hover bearable rather
 * than infuriating:
 *
 *   - an OPEN DELAY, so dragging the pointer across a timeline of fifty avatars
 *     does not fire fifty cards;
 *   - a CLOSE DELAY plus the card itself keeping the card alive, so the gap
 *     between the avatar and the card is crossable — without it the card
 *     vanishes the moment you reach for the button you came to press;
 *   - TOUCH IS EXCLUDED. A touch "hover" is really a tap, and a tap already
 *     opens the card through the button. Opening on both would fire twice.
 *
 * Hover-opening also must not steal focus: the card appears under the pointer,
 * not because anyone asked to interact with it, so a hover-open leaves the
 * caret exactly where the reader left it.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { cn } from '@cyphex/ui';
import type { UserId } from '@cyphex/types';
import { ProfileCard } from './ProfileCard';

/** Long enough to survive a pointer crossing the avatar on its way elsewhere. */
const OPEN_DELAY_MS = 320;
/** Long enough to cross the gap from the avatar to the card. */
const CLOSE_DELAY_MS = 180;

export interface ProfileCardPopoverProps {
  userId: UserId;
  /** Shown in the card's header while the profile is still loading. */
  displayName: string;
  avatarUrl?: string | null;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Extra classes for the trigger button, not the card. */
  className?: string;
}

export function ProfileCardPopover({
  userId,
  displayName,
  avatarUrl,
  children,
  side = 'bottom',
  align = 'start',
  className,
}: ProfileCardPopoverProps) {
  const [open, setOpen] = useState(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /* Whether THIS opening came from the pointer, which decides if the card is
     allowed to take focus when it mounts. */
  const byPointer = useRef(false);

  const clearTimers = useCallback(() => {
    if (openTimer.current !== null) clearTimeout(openTimer.current);
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  /* A timer that fires after the row has been unmounted would set state on a
     dead component and, worse, leave a card open over a timeline that has since
     scrolled somewhere else. */
  useEffect(() => clearTimers, [clearTimers]);

  const hoverOpen = useCallback(
    (event: React.PointerEvent) => {
      if (event.pointerType === 'touch') return;
      clearTimers();
      openTimer.current = setTimeout(() => {
        byPointer.current = true;
        setOpen(true);
      }, OPEN_DELAY_MS);
    },
    [clearTimers],
  );

  const hoverClose = useCallback(
    (event: React.PointerEvent) => {
      if (event.pointerType === 'touch') return;
      clearTimers();
      closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
    },
    [clearTimers],
  );

  const keepOpen = useCallback(() => clearTimers(), [clearTimers]);

  const onOpenChange = useCallback(
    (next: boolean) => {
      clearTimers();
      /* A click (or Enter) is a deliberate open and MAY take focus. */
      if (next) byPointer.current = false;
      setOpen(next);
    },
    [clearTimers],
  );

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`View profile for ${displayName}`}
          onPointerEnter={hoverOpen}
          onPointerLeave={hoverClose}
          className={cn(
            'inline-flex max-w-full items-center rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          {children}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={12}
          aria-label={`Profile card for ${displayName}`}
          onPointerEnter={keepOpen}
          onPointerLeave={hoverClose}
          onOpenAutoFocus={(event) => {
            if (byPointer.current) event.preventDefault();
          }}
          className="z-dropdown animate-scale-in rounded-2xl border border-border bg-popover text-popover-foreground shadow-dropdown"
        >
          <ProfileCard
            userId={userId}
            fallbackName={displayName}
            fallbackAvatarUrl={avatarUrl ?? null}
            onDismiss={() => setOpen(false)}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
